import type {
  AnalyticsColorTone,
  ConfiguratorFormatCategory,
} from './types'

export const specificUseFilterProperties = {
  filter_name: 'uso_especifico',
  step_number: 1,
} as const

export const formatFilterProperties = {
  filter_name: 'formatos',
  step_number: 2,
} as const

export const designFilterProperties = {
  filter_name: 'disenos',
  step_number: 3,
} as const

export const colorFilterProperties = {
  filter_name: 'colores',
  step_number: 4,
} as const

export const customColorFilterProperties = {
  filter_name: 'personalizar',
  step_number: 5,
} as const

export function buildSpecificUseFilterEventName(
  specificationName: string,
  fallbackValue: string,
) {
  const specificationSlug = buildAnalyticsSlug(specificationName, fallbackValue)

  return {
    eventName: `cfg_filtro_uso_especifico_${specificationSlug}` as const,
    specificationSlug,
  }
}

export function buildFormatFilterEventName(
  formatCategory: ConfiguratorFormatCategory,
  formatName: string,
  fallbackValue: string,
) {
  const formatSlug = buildAnalyticsSlug(formatName, fallbackValue)

  return {
    eventName: `cfg_filtro_formatos_${formatCategory}_${formatSlug}` as const,
    formatSlug,
  }
}

export function buildDesignFilterEventName(
  designName: string,
  fallbackValue: string,
) {
  const designSlug = buildAnalyticsSlug(designName, fallbackValue)

  return {
    eventName: `cfg_filtro_disenos_${designSlug}` as const,
    designSlug,
  }
}

export function buildColorFilterEventName(
  colorConfigurationName: string,
  fallbackValue: string,
) {
  const colorConfigurationSlug = buildAnalyticsSlug(
    colorConfigurationName,
    fallbackValue,
  )

  return {
    eventName: `cfg_filtro_colores_${colorConfigurationSlug}` as const,
    colorConfigurationSlug,
  }
}

export function buildCustomColorFilterEventName(
  areaNumber: string,
  toneName: string,
  fallbackValue: string,
) {
  const toneSlug = buildAnalyticsSlug(toneName, fallbackValue)

  return {
    eventName: `cfg_filtro_personalizar_${areaNumber}_${toneSlug}` as const,
    toneSlug,
  }
}

export function getColorToneAnalyticsProperties(tones: {
  area: string
  tone: {
    hex: string
    id: string
    name: string
  }
}[]): AnalyticsColorTone[] {
  return tones.map(({ area, tone }) => ({
    area,
    tone_hex: tone.hex,
    tone_id: tone.id,
    tone_name: tone.name,
  }))
}

export function parseAnalyticsNumber(value: number | string) {
  if (typeof value === 'number') {
    return value
  }

  const parsed = Number.parseFloat(value.replace(',', '.'))
  return Number.isNaN(parsed) ? 0 : parsed
}

export function getCartSummaryAnalyticsProperties({
  cartItems,
  summary,
}: {
  cartItems: {
    area?: string
    quantity?: string
  }[]
  summary?: {
    payment_confirmation_amount?: null | number
    subtotal?: number
    total_with_vat?: null | number
  } | null
}) {
  const totalQuantity = cartItems.reduce((total, item) => {
    const quantity = item.area || item.quantity || '0'
    return total + parseAnalyticsNumber(quantity)
  }, 0)

  return {
    items_count: cartItems.length,
    payment_confirmation_amount:
      summary?.payment_confirmation_amount ?? undefined,
    subtotal: summary?.subtotal,
    total_quantity: totalQuantity,
    total_with_vat: summary?.total_with_vat ?? undefined,
  }
}

export function buildAnalyticsSlug(value: string, fallbackValue: string) {
  return slugifyAnalyticsValue(value) || slugifyAnalyticsValue(fallbackValue)
}

function slugifyAnalyticsValue(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}
